/**
 * Dashboard page with overview statistics.
 * 
 * @module pages/DashboardPage
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, FileText, TrendingUp, ArrowRight } from 'lucide-react';
import { StatCard, Card, Badge, StatsGridSkeleton, TableSkeleton } from '../components/ui';
import { useDashboardStats, useRecentReports, useExclusionRules } from '../hooks';
import { isExcludedUser } from '../config/excluded-users';
import { useAuditLog } from '../hooks/useAuditLog';
import { useSettings } from '../context/SettingsContext';
import { AuditActionTypes } from '../lib/audit';
import { cn, formatDate, formatNumber } from '../lib/utils';

/**
 * Dashboard page component.
 * 
 * @returns Dashboard page element
 */
export function DashboardPage() {
  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats();
  const { data: recentReports, isLoading: reportsLoading } = useRecentReports(10);
  const { logAction } = useAuditLog();
  const { hideInternalStats } = useSettings();
  const [reportsFilter, setReportsFilter] = useState<'external' | 'all'>('external');
  const { data: exclusionRules } = useExclusionRules();

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_DASHBOARD);
  }, [logAction]);

  const filteredReports = recentReports?.filter((report) => {
    if (reportsFilter === 'all') return true;
    return !isExcludedUser(report.users?.email, exclusionRules);
  });

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">
          Overview of Clarity Search AI platform statistics
        </p>
      </div>

      {/* Stats grid */}
      {statsLoading ? (
        <StatsGridSkeleton count={4} />
      ) : statsError ? (
        <Card>
          <p className="text-error-600">Failed to load statistics</p>
        </Card>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Users"
            value={formatNumber(stats.totalUsers)}
            icon={<Users className="h-6 w-6" />}
            subtitle={!hideInternalStats && stats.excludedUsers > 0 ? `${stats.excludedUsers} internal excluded` : undefined}
          />
          <StatCard
            title="Paid Users"
            value={formatNumber(stats.paidUsers)}
            icon={<TrendingUp className="h-6 w-6" />}
            subtitle={!hideInternalStats && stats.excludedPaidUsers > 0 ? `${stats.excludedPaidUsers} internal excluded` : undefined}
          />
          <StatCard
            title="Total Businesses"
            value={formatNumber(stats.totalBusinesses)}
            icon={<Building2 className="h-6 w-6" />}
            subtitle={!hideInternalStats && stats.excludedBusinesses > 0 ? `${stats.excludedBusinesses} internal excluded` : undefined}
          />
          <StatCard
            title="Total Reports"
            value={formatNumber(stats.totalReports)}
            icon={<FileText className="h-6 w-6" />}
            subtitle={!hideInternalStats && stats.excludedReports > 0 ? `${stats.excludedReports} internal excluded` : undefined}
          />
        </div>
      ) : null}

      {/* Plan distribution */}
      {stats && stats.planDistribution.length > 0 && (
        <Card
          title="Users by Plan"
          action={
            !hideInternalStats && stats.excludedUsers > 0 ? (
              <span className="text-xs text-gray-400">
                {stats.excludedUsers} internal excluded
              </span>
            ) : undefined
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.planDistribution.map((plan) => (
              <div key={plan.planId} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{plan.userCount}</p>
                <p className="text-sm text-gray-500">{plan.planName}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent reports */}
      <Card
        title="Recent Analysis Reports"
        action={
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
              <button
                onClick={() => setReportsFilter('external')}
                className={cn(
                  'px-2.5 py-1 rounded-md font-medium transition-colors',
                  reportsFilter === 'external'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                External
              </button>
              <button
                onClick={() => setReportsFilter('all')}
                className={cn(
                  'px-2.5 py-1 rounded-md font-medium transition-colors',
                  reportsFilter === 'all'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                All
              </button>
            </div>
            <Link
              to="/reports"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        }
      >
        {reportsLoading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : filteredReports && filteredReports.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>AI Score</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td>
                      <Link
                        to={`/businesses/${report.business_id}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {report.businesses?.business_name || '—'}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/users/${report.profile_id}`}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        {report.users?.email || '—'}
                      </Link>
                    </td>
                    <td>
                      <Badge
                        variant={
                          report.status === 'completed'
                            ? 'success'
                            : report.status === 'failed'
                            ? 'error'
                            : report.status === 'running'
                            ? 'warning'
                            : 'neutral'
                        }
                        size="sm"
                      >
                        {report.status}
                      </Badge>
                    </td>
                    <td>
                      {report.avg_ai_visibility_score !== null
                        ? `${(report.avg_ai_visibility_score * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td className="text-gray-500">{formatDate(report.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No recent reports</p>
        )}
      </Card>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/users"
          className="card p-6 hover:border-primary-300 hover:shadow-md transition-all group"
        >
          <Users className="h-8 w-8 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600">
            View All Users
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Browse user accounts and subscription details
          </p>
        </Link>

        <Link
          to="/businesses"
          className="card p-6 hover:border-primary-300 hover:shadow-md transition-all group"
        >
          <Building2 className="h-8 w-8 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600">
            View All Businesses
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Browse business configurations and reports
          </p>
        </Link>

        <Link
          to="/audit-logs"
          className="card p-6 hover:border-primary-300 hover:shadow-md transition-all group"
        >
          <FileText className="h-8 w-8 text-primary-600 mb-3" />
          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600">
            Audit Logs
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            View admin activity and access logs
          </p>
        </Link>
      </div>
    </div>
  );
}
